import type { CompositionEntry } from '@internal-audit/domain';

/**
 * Color palettes for different composition types
 * Extracted from repeated inline arrays to ensure consistency
 */
export const COMPOSITION_COLORS = {
  labels: ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'],
  languages: ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'],
  topics: ['#06b6d4', '#f97316', '#ec4899', '#84cc16', '#a855f7'],
} as const;

/**
 * Calculate percentage for composition entries
 * Extracted from repeated inline calculations
 */
export const calculatePercentage = (count: number, total: number): number => (total > 0 ? Math.round((count / total) * 100) : 0);

/**
 * Get color for composition entry by index and type
 */
export const getCompositionColor = (index: number, type: keyof typeof COMPOSITION_COLORS): string => {
  const colors = COMPOSITION_COLORS[type];

  return colors[index % colors.length];
};

/**
 * Format composition entry for display
 */
export const formatCompositionEntry = (
  entry: CompositionEntry,
  options: {
    totalItems: number;
    colorType: keyof typeof COMPOSITION_COLORS;
    index: number;
  }
) => {
  const { totalItems, colorType, index } = options;
  const percentage = calculatePercentage(entry.count, totalItems);
  const color = getCompositionColor(index, colorType);

  return {
    key: entry.key,
    count: entry.count,
    percentage,
    color,
    displayText: `${entry.count} (${percentage}%)`,
  };
};

/**
 * Constants for magic numbers used throughout the component
 */
export const COMPOSITION_CONSTANTS = {
  MAX_FILTER_BADGES: {
    languages: 3,
    topics: 2,
    labels: 3,
    severities: 3,
    contentTypes: 3,
  },
  PANEL_HEIGHT: '200px',
  PROGRESS_BAR_WIDTH: '120px',
} as const;
